import { IconInfoCircle } from '@tabler/icons-react'
import uniqBy from 'lodash/uniqBy'
import { useShallow } from 'zustand/react/shallow'

import { AstraButton, AstraCheckbox, AstraCheckboxProps, AstraChip, AstraHeading, AstraParagraph } from '~/astra'
import { useStore } from '~/store/store'
import { RecurringAssignmentType, useActiveRecurringAssignments } from '~/storm'

import { allRecurringAssignmentRoomDays, allRecurringAssignmentStatusDays } from '../../shared/utils'
import {
    pluralizeAssignmentCount,
    RecurringAssignmentFilteredListMessage,
    RecurringAssignmentFilters,
    useRecurringAssignmentFilters,
} from './RecurringAssignmentFilters'
import { RecurringAssignmentHeader } from './RecurringAssignmentHeader'

const RecurringAssignmentToggleElement = ({
    assignment,
    onToggleAssignment,
    isChecked,
}: {
    assignment: RecurringAssignmentType
    onToggleAssignment: (assignment: RecurringAssignmentType) => void
    isChecked: boolean
}) => {
    const uniqueStatuses = uniqBy(allRecurringAssignmentStatusDays(assignment).filter(Boolean), 'code')
    const uniqueRooms = uniqBy(allRecurringAssignmentRoomDays(assignment).filter(Boolean), 'room_code')

    return (
        <div
            className="no-user-select flex cursor-pointer items-center gap-4 rounded-lg border border-base p-2.5"
            onClick={() => onToggleAssignment(assignment)}
            role="checkbox"
            aria-checked={isChecked}
            tabIndex={0}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onToggleAssignment(assignment)
                }
            }}
        >
            <AstraCheckbox checked={isChecked} />
            <RecurringAssignmentHeader uniqueStatuses={uniqueStatuses} uniqueRooms={uniqueRooms} assignment={assignment} />
        </div>
    )
}

type Props = {
    onApply: () => void
    disabledApplyButton: boolean
}

export const RecurringAssignmentToggleList = ({ onApply, disabledApplyButton }: Props) => {
    const activeRecurringAssignments = useActiveRecurringAssignments()
    const { filters, handleSetFilter, filteredAssignments, isAnyFilterActive } = useRecurringAssignmentFilters(activeRecurringAssignments)

    const [deselectedRecurringAssignmentUuids, setDeselectedRecurringAssignmentUuids] = useStore(
        useShallow(store => [store.surgeonScheduler.deselectedRecurringAssignmentUuids, store.surgeonScheduler.actions.setDeselectedRecurringAssignmentUuids])
    )
    const deselectedSet = new Set(deselectedRecurringAssignmentUuids)

    const setDeselected = (callback: (previousIds: Set<string>) => Set<string>) => {
        const previousDeselectedSet = new Set(useStore.getState().surgeonScheduler.deselectedRecurringAssignmentUuids)
        const newDeselected = callback(previousDeselectedSet)
        setDeselectedRecurringAssignmentUuids(Array.from(newDeselected))
    }

    const handleToggleAssignment = (assignment: RecurringAssignmentType) => {
        setDeselected(previousIds => {
            if (previousIds.has(assignment.uuid)) {
                previousIds.delete(assignment.uuid)
            } else {
                previousIds.add(assignment.uuid)
            }
            return previousIds
        })
    }

    const handleSelectAll = () => {
        setDeselected(previousIds => {
            filteredAssignments.forEach(assignment => {
                previousIds.delete(assignment.uuid)
            })
            return previousIds
        })
    }

    const handleDeselectAll = () => {
        setDeselected(previousIds => {
            filteredAssignments.forEach(assignment => {
                previousIds.add(assignment.uuid)
            })
            return previousIds
        })
    }

    const selectedCount = activeRecurringAssignments.length - deselectedRecurringAssignmentUuids.length
    const visibleSelectedCount = filteredAssignments.filter(a => !deselectedSet.has(a.uuid)).length

    const allVisibleSelected = visibleSelectedCount > 0 && visibleSelectedCount === filteredAssignments.length
    const allVisibleDeselected = visibleSelectedCount === 0

    const checkedStatus: AstraCheckboxProps['checked'] = !allVisibleSelected && !allVisibleDeselected ? 'indeterminate' : allVisibleSelected

    const hiddenByFilterCount = activeRecurringAssignments.length - filteredAssignments.length
    const hiddenSelectedCount = selectedCount - visibleSelectedCount
    return (
        <div className="just flex h-full shrink-0 flex-col gap-4 align-middle">
            <div>
                <AstraHeading as="h2">Valgte regler</AstraHeading>
            </div>
            {activeRecurringAssignments.length > 0 && (
                <div className="flex flex-col gap-3">
                    <RecurringAssignmentFilters recurringAssignments={activeRecurringAssignments} filters={filters} onFilterChange={handleSetFilter} />
                    <div className="flex min-h-6 items-center gap-2 pl-2">
                        <AstraCheckbox
                            id="selected-assignments"
                            checked={checkedStatus}
                            name="selected-assignments"
                            onCheckedChange={newCheckedState => (newCheckedState === true ? handleSelectAll() : handleDeselectAll())}
                            disabled={filteredAssignments.length === 0}
                        />
                        <label htmlFor="selected-assignments">
                            <AstraParagraph>
                                {visibleSelectedCount} av {filteredAssignments.length} {pluralizeAssignmentCount(visibleSelectedCount)} valgt
                            </AstraParagraph>
                        </label>
                        {hiddenSelectedCount > 0 && <AstraChip variant="filled" color="info" label={`+${hiddenSelectedCount} valg skjult av filtre`} />}
                    </div>
                </div>
            )}
            <div className="flex flex-col gap-4 overflow-y-auto">
                {filteredAssignments.map(assignment => (
                    <RecurringAssignmentToggleElement
                        key={assignment.uuid}
                        assignment={assignment}
                        isChecked={!deselectedSet.has(assignment.uuid)}
                        onToggleAssignment={handleToggleAssignment}
                    />
                ))}
                {isAnyFilterActive && (
                    <RecurringAssignmentFilteredListMessage
                        filteredAssignments={filteredAssignments}
                        hiddenByFilterCount={hiddenByFilterCount === hiddenSelectedCount ? 0 : hiddenByFilterCount}
                    />
                )}
            </div>
            {selectedCount === 0 && (
                <div className="self-center">
                    <AstraChip variant="filled" color="info" icon={<IconInfoCircle />} label="Du må velge minst én regel for å fylle inn i planen." />
                </div>
            )}

            <div>
                <AstraButton size="large" label="Fyll inn i planen" fullWidth onClick={onApply} disabled={disabledApplyButton || selectedCount === 0} />
            </div>
        </div>
    )
}
